<template>
  <div id="OngoingTestsView" class="ongoing-tests-view">
    <Header></Header>
    <b-container fluid>
      <b-row>
        <LeftNav></LeftNav>
        <b-col>
          <b-row>
            <TestsTabs isOngoing></TestsTabs>
          </b-row>
          <b-row class="justify-content-end mt-5 mb-3">
            <b-col sm="3">
              <b-form-input v-model="tableFilter" type="text" placeholder="Filter"></b-form-input>
            </b-col>
            <b-col sm="2">
              <b-button variant="primary" @click.prevent="showModal" :disabled="selectedTests.length === 0">Stop test</b-button>
            </b-col>
            <b-col sm="1">
              <a class="icon-container pointer" @click.prevent="refresh">
                <i class="material-icons text-muted" :class="{ rotating: isRefreshing }">refresh</i>
              </a>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="pl-sm-5">
              <b-table striped
                :items="tests"
                :fields="fields"
                :filter="tableFilter"
                @sort-changed="sortingChanged"
                :sort-compare="compareRows"
                :current-page="currentPage"
                :per-page="perPage"
                :sort-direction="sortDirection"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc">
                <template slot="select" slot-scope="data">
                  <p-check class="p-icon p-smooth" name="toProcess" color="info" v-model="selectedTests" :value="data.item.id" style="font-size: 16px;">
                    <i slot="extra" class="icon material-icons">done</i>
                  </p-check>
                </template>
                <template slot="name" slot-scope="data">
                  <router-link :to="{ name: 'testdetails', params: { id: data.item.id } }">{{data.item.name}}</router-link>
                </template>
                <template slot="countryId" slot-scope="data">
                  {{ getCountryById(data.item.countryId).name }}
                </template>
                <template slot="carrierpercentage" slot-scope="data">
                  {{ getCarrierById(data.item.carrierId).name }} ({{data.item.percentage}}%)
                </template>
                <template slot="start" slot-scope="data">
                  {{data.value | formatDate}}
                </template>
                <template slot="end" slot-scope="data">
                  {{data.value | formatDate}}
                </template>
              </b-table>
              <b-pagination v-if="isPaginationShown()" align="center" :total-rows="tests.length" :per-page="perPage" v-model="currentPage"></b-pagination>
            </b-col>
          </b-row>
        </b-col>
      <!--<TestsFilter></TestsFilter>-->
      </b-row>
    </b-container>
    <b-modal id="modal-center" centered title="Are you sure?" footer-class="d-flex justify-content-center border-0">
      <div class="modal-body">
        <p>Are you sure you want to stop this test(s)?</p>
      </div>
      <div slot="modal-footer">
        <b-button variant="primary" size="lg" @click.prevent="stop">Yes, Stop test(s)</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { sortByDate, sortByCountry, sortByCarrier, sortByString } from '@/util/sorting'
import LeftNav from '@/components/layout/LeftNav.vue'
import Header from '@/components/layout/Header.vue'
import TestsTabs from '@/components/TestsTabs.vue'
import TestsFilter from '@/components/TestsFilter.vue'

export default {
  name: 'OngoingTest',
  components: {
    LeftNav,
    Header,
    TestsTabs,
    TestsFilter
  },
  created () {
    this.loadCountries()
    this.loadCarriers()
    this.loadTests()
  },
  mounted () {
    if ('currentPage' in this.$route.params) {
      this.currentPage = parseInt(this.$route.params.currentPage)
    }
  },
  methods: {
    ...mapActions({
      'loadTests': 'tests/loadTests',
      'fetchTests': 'tests/fetchTests',
      'stopTest': 'tests/stopTest',
      'loadCarriers': 'tests/loadCarriers',
      'loadCountries': 'countries/loadCountries'
    }),
    compareRows (a, b) {
      if (this.sortBy === 'start') {
        return sortByDate(this.sortDesc, a.start, b.start)
      } else if (this.sortBy === 'end') {
        return sortByDate(this.sortDesc, a.end, b.end)
      } else if (this.sortBy === 'countryId') {
        return sortByCountry(this.sortDesc, a.countryId, b.countryId)
      } else if (this.sortBy === 'carrierpercentage') {
        return sortByCarrier(this.sortDesc, a.carrierId, b.carrierId)
      }

      return sortByString(this.sortBy, this.sortDesc, a, b)
    },
    sortingChanged (ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
    },
    async stop () {
      if (this.selectedTests.length === 0) {
        this.$toastr.w('Please select a test')
      } else {
        for (let id of this.selectedTests) {
          try {
            await this.stopTest({id: id})
            this.$toastr.s('Test(s) stopped')
          } catch (ex) {
            console.error(ex)
            this.$toastr.e('Error stopping test')
          }
        }
        this.selectedTests = []
        await this.fetchTests()
        this.$root.$emit('bv::hide::modal', 'modal-center')
      }
    },
    refresh () {
      this.isRefreshing = true
      this.fetchTests().then(() => {
        this.isRefreshing = false
      })
    },
    showModal () {
      this.$root.$emit('bv::show::modal', 'modal-center')
    },
    isPaginationShown () {
      return this.tests.length > this.perPage
    }
  },
  computed: {
    ...mapGetters({
      'tests': 'tests/ongoingTests',
      'getCarrierById': 'tests/getCarrierById',
      'getCountryById': 'countries/getCountryById'
    })
  },
  watch: {
    currentPage: {
      handler (newValue) {
        this.$router.push({name: 'ongoingtestswithpage', params: {'currentPage': newValue}})
      }
    }
  },
  data () {
    return {
      selectedTests: [],
      sortBy: 'end',
      sortDesc: true,
      sortDirection: 'asc',
      perPage: 10,
      currentPage: 1,
      tableFilter: null,
      isRefreshing: false,
      fields: [
        { key: 'select', label: '' },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'countryId', label: 'Country', sortable: true },
        { key: 'breakout', label: 'Breakout', sortable: true },
        { key: 'carrierpercentage', label: 'Carrier', sortable: true },
        { key: 'start', label: 'Start Date', sortable: true },
        { key: 'end', label: 'End Date', sortable: true }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/views/tests/OngoingTests.vue